/**
 * What the roster shows, given what the trainer asked for: the tab, the search, the order.
 *
 * `GET /customer` has no unified search parameter, no sort parameter and no pagination — its three
 * name/email filters are exact-match server-side — so the search and the order are ours, over the
 * whole roster, with no alternative.
 *
 * ── The tab is ours by CHOICE, and the earlier note about why was false ──────────────────────
 * It claimed `?status=invited` returns nothing because the processor "only appends pending
 * invitations when no status filter is supplied". Read the source: `CustomerProcessor.ListAsync`
 * step 3 branches on `status == CustomerStatus.Invited` and returns the trainer's pending offers as
 * a pure invitation view, skipping the customer query entirely. The filter parses through
 * `EnumName.TryParse`, which is case-insensitive, so the lower-case value the roster already speaks
 * would match. The server can answer this question.
 *
 * The tab stays client-side anyway, for a reason that survives the correction: all four tabs are
 * views of ONE cached response, and a server-side tab would mean a second request, a second cache
 * key and a second loading state every time the trainer touches a radio — while the "Wszyscy" read
 * already contains every row all four of them need. What it would additionally cost is the count in
 * the header, which is folded from the same response.
 *
 * One bound worth knowing: that unfiltered read appends at most `RosterPendingOfferCap = 500`
 * pending offers, described in the backend as a backstop against an unlimited-plan trainer rather
 * than a page to walk. Nothing here pages, so a trainer past 500 outstanding offers would see the
 * first 500.
 *
 * Pure, and the order is stable-by-construction rather than by trusting the engine: the roster
 * arrives in whatever order Postgres felt like (`CustomerRepository.GetAllAsync` has no
 * `OrderBy`), so every comparator falls back to the row's name and then its key. Without that a
 * re-fetch reorders rows that compare equal, and the table appears to shuffle on its own.
 *
 * ── The canon replaced sortable column headers with ONE control ──────────────────────────────
 * KANON 01 draws the column labels as plain text and puts a single selector on the right of the
 * toolbar, reading "Wymagający uwagi najpierw". So order is a roster-wide question with four
 * answers, not a per-column toggle, and `RosterOrder` is that question. This also settles the
 * concern recorded against the 10.08 brief — that an at-risk sort "skakał między odświeżeniami" —
 * because the tie-break below is total rather than left to arrival order.
 */

import type { ClientRow } from './clientRow';
import type { ClientStanding } from './planStanding';

/**
 * The four tabs KANON 01 draws — and the fourth one is not a status.
 *
 * `CustomerStatus` is **binary**: `Data/Enums/CustomerStatus.cs` holds `{ Invited, Active }`, and
 * the `Inactive` member was dropped by the 2026-08-04 archiving ADR along with its migration. So a
 * "Nieaktywni" tab written as a status filter can never match a row, and a tab that can never
 * match is the dead affordance this repo refuses to draw.
 *
 * It is read as behaviour instead: a client who has not finished a workout inside the backend's own
 * lapse window. That is the reading the canon's own sample copy takes — its third row's sub-line is
 * "Nieaktywny 41 dni", which is `LapsedActivity` phrased for a person — and it is the only reading
 * that makes the control do anything. **Raised for the owner** rather than assumed settled: it is
 * the one place the canon left a control's meaning open.
 */
export type RosterTab = 'all' | 'active' | 'invited' | 'lapsed';

/**
 * The backend's own threshold, not a number picked here: `AttentionWindows.LapsedActivityDays`, and
 * the tab must agree with the risk line beside it or the two disagree about the same client on the
 * same screen.
 *
 * **CORRECTED 16 September 2026, from 14 to 7.** The claim above was true when it was written and
 * stopped being true on 24 August 2026, when the owner tuned the backend down — its own comment:
 * "two weeks of silence is long past the point where reaching out still rescues the client, so the
 * nudge lands after one missed training week instead". Nothing carried that number across the wire,
 * so this constant kept its old value while the sentence above it went on claiming it was the
 * backend's.
 *
 * What it cost, caught on the live stack: a client who last trained NINE days ago carried the red
 * "Brak aktywności" line, which the backend's queue was right to raise, and was absent from the
 * "Nieaktywni" tab, which is the exact contradiction this comment exists to forbid. Every client
 * between seven and thirteen days of silence was in that hole.
 *
 * It is a number the wire does not carry, so it can drift again. If a third surface needs it, that
 * is the point at which it should become a field on a response rather than a third copy.
 */
const LAPSED_AFTER_DAYS = 7;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The orderings the table offers. The string is the URL-facing value.
 *
 * **SIX since 16 September 2026, and they are reached from the COLUMN HEADERS.** Owner's
 * instruction was to take the filter control off the toolbar and put ordering on the headers
 * instead, so `sleep` and `mood` joined — those two columns now have somewhere to be clicked, and
 * both means are already on the row, so the pair cost a comparator each.
 *
 * `attention` is the default and is the one ordering NO column carries: it is a roster-wide
 * question ("who needs me first") rather than a property of any single column. The table returns to
 * it when the active header is clicked a second time, which is what keeps it reachable without a
 * control of its own. See `RosterHeadRow`.
 */
export type RosterOrder = 'attention' | 'name' | 'compliance' | 'lastWorkout' | 'sleep' | 'mood';

const ROSTER_ORDERS: readonly RosterOrder[] = [
  'attention',
  'name',
  'compliance',
  'lastWorkout',
  'sleep',
  'mood',
];
const ROSTER_TABS: readonly RosterTab[] = ['all', 'active', 'invited', 'lapsed'];

/**
 * Narrow a string — a saved preference, a query parameter — back to a domain value. A cast there
 * would let a stale value select nothing and say nothing about it.
 */
export function isRosterOrder(value: string): value is RosterOrder {
  return (ROSTER_ORDERS as readonly string[]).includes(value);
}

export function isRosterTab(value: string): value is RosterTab {
  return (ROSTER_TABS as readonly string[]).includes(value);
}

/** KANON draws these two selected. */
export const DEFAULT_ROSTER_ORDER: RosterOrder = 'attention';
export const DEFAULT_ROSTER_TAB: RosterTab = 'all';

export interface RosterQuery {
  /** Free text, matched against name, e-mail and goal. Empty means no text filter. */
  readonly search: string;
  readonly tab: RosterTab;
  readonly order: RosterOrder;
  /** Polish collation is not the browser's business to guess. */
  readonly locale: string;
  /** The clock the "lapsed" tab is read against. A parameter, so a test can ask about any day. */
  readonly now: Date;
}

/**
 * Fold a string to something a search box can match.
 *
 * Polish is the source language, so a trainer types "dabrowska" and expects "Dąbrowska". NFD
 * decomposition plus stripping combining marks handles ą ć ę ń ó ś ź ż, but **not `ł`** — U+0142
 * is a precomposed letter with a stroke, not a base plus a mark, so it survives NFD untouched and
 * "walesa" would miss "Wałęsa". The two stroke letters are therefore mapped by hand. This is the
 * shortest correct rule for Polish; it is not a general transliterator.
 */
function foldForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/gu, '')
    .replace(/ł/gu, 'l')
    .replace(/Ł/gu, 'L')
    .toLowerCase();
}

/**
 * Name, e-mail and GOAL. The goal is in here because the canon's placeholder promises it —
 * "Szukaj klienta lub celu" — and a search box that silently ignores half of what it offers is
 * worse than one that offers less.
 */
function matchesSearch(row: ClientRow, foldedTerm: string): boolean {
  if (foldedTerm === '') return true;
  if (foldForSearch(row.fullName).includes(foldedTerm)) return true;
  if (row.email !== null && foldForSearch(row.email).includes(foldedTerm)) return true;
  return row.goal !== null && foldForSearch(row.goal).includes(foldedTerm);
}

/**
 * Has this client stopped training?
 *
 * A pending invitation is never lapsed — nobody has joined yet, so there is no activity to have
 * lapsed. Neither is a client with nothing scheduled in the window: a trainer who added somebody
 * this morning has not lost them, and the tab asks who STOPPED. An accepted client who was scored
 * and has never finished a workout IS lapsed, because that is exactly the person the tab exists to
 * surface — which is why the two absences are told apart in `clientRow` rather than collapsed.
 *
 * ── A self-paced client is judged on their PLAN's clock ───────────────────────────────────────
 * The two guards above are both compliance-window questions, and this mode has no compliance
 * window: the backend sends `complianceTotal` as null rather than 0, so `nothingDue` is false, and
 * the rule fell straight through to "no completed workout ever, therefore lapsed". A client
 * assigned a self-paced plan this morning was filed under Nieaktywni while the standing cell two
 * columns away said their plan was waiting for its first session.
 *
 * `lastWorkoutAt` is not the repair either: it is GLOBAL, so it can carry a workout from a plan
 * that ended last spring and would call a stalled client active. The standing is plan-scoped and
 * is the answer the backend computed for exactly this question, so it is the one asked.
 */
function isLapsed(row: ClientRow, now: Date): boolean {
  if (row.status === 'invited') return false;
  if (row.nothingDue) return false;
  if (row.standing !== null) return standingHasLapsed(row.standing);
  if (row.lastWorkoutAt === null) return true;
  const last = Date.parse(row.lastWorkoutAt);
  if (Number.isNaN(last)) return true;
  return now.getTime() - last > LAPSED_AFTER_DAYS * MILLISECONDS_PER_DAY;
}

/**
 * Has a self-paced client's plan stood still long enough to call it stopped?
 *
 * One threshold for both modes — a fortnight is a fortnight — applied to whichever elapsed time
 * this client actually has. A client who has trained is measured from that; one who has not is
 * measured from how long their cursor has stood, which is zero on the day the plan was assigned and
 * is exactly the client the tab must not claim.
 *
 * `waitingDays` is dropped by `clientStanding` at zero and where it merely repeats the recency
 * figure, so the `?? 0` reads "no wait worth stating", which is never a lapse.
 */
function standingHasLapsed(standing: ClientStanding): boolean {
  if (standing.recency.kind === 'trained') return standing.recency.days > LAPSED_AFTER_DAYS;
  return (standing.waitingDays ?? 0) > LAPSED_AFTER_DAYS;
}

function matchesTab(row: ClientRow, tab: RosterTab, now: Date): boolean {
  switch (tab) {
    case 'all':
      return true;
    case 'active':
      return row.status === 'active';
    case 'invited':
      return row.status === 'invited';
    case 'lapsed':
      return isLapsed(row, now);
  }
}

/**
 * The bands "Wymagający uwagi najpierw" sorts into.
 *
 * Risk first, invitations last, everyone else in between — the mobile list's own order, and the
 * two things KANON 01 shows without ambiguity. Inside a band it is alphabetical, because any
 * finer ranking would be reading intent into a mockup's nine sample rows.
 */
function attentionBand(row: ClientRow): number {
  if (row.riskReason !== null) return 0;
  return row.status === 'invited' ? 2 : 1;
}

/**
 * Compare two possibly-absent values so that **absence always sinks**, in both directions.
 *
 * This is the roster's one sorting rule, and it is deliberate. A client with no attendance figure
 * has not scored 0 %; a client with no last workout has not trained "infinitely long ago" — a
 * pending invitation has no workouts because nobody has joined yet. Letting either masquerade as
 * an extreme puts the least informative rows at the top of exactly the sort a trainer runs to find
 * the worst cases.
 */
function compareOptional<T>(
  a: T | null,
  b: T | null,
  compare: (a: T, b: T) => number,
): { readonly result: number; readonly bothPresent: boolean } {
  if (a === null && b === null) return { result: 0, bothPresent: false };
  if (a === null) return { result: 1, bothPresent: false };
  if (b === null) return { result: -1, bothPresent: false };
  return { result: compare(a, b), bothPresent: true };
}

/**
 * Narrow the roster to the tab and the search term.
 *
 * Separate from the order because the screen needs the count of what survived to tell "you have no
 * clients" apart from "your filter matched none of them", and those are different screens.
 */
export function filterClientRows(
  rows: readonly ClientRow[],
  query: Pick<RosterQuery, 'search' | 'tab' | 'now'>,
): readonly ClientRow[] {
  const foldedTerm = foldForSearch(query.search.trim());
  if (foldedTerm === '' && query.tab === 'all') return rows;
  return rows.filter(
    (row) => matchesTab(row, query.tab, query.now) && matchesSearch(row, foldedTerm),
  );
}

/**
 * Order the roster.
 *
 * Every branch ends at the same total tie-break — name, then row key — so two rows that compare
 * equal keep a fixed order across re-fetches.
 */
export function orderClientRows(
  rows: readonly ClientRow[],
  order: RosterOrder,
  locale: string,
): ClientRow[] {
  // Default sensitivity ('variant'), deliberately: Polish collation puts `Ł` between `L` and `M`
  // and `Ó` beside `O`, and folding accents away here would tie names the trainer sees as
  // different and hand the order to the key tie-break instead.
  const collator = new Intl.Collator(locale, { numeric: true });

  const primary = (rowA: ClientRow, rowB: ClientRow): number => {
    switch (order) {
      case 'attention':
        return attentionBand(rowA) - attentionBand(rowB);
      case 'name':
        return 0;
      case 'compliance': {
        // Worst first: this is the order a trainer chooses in order to find the people who are
        // slipping, so ascending is the only useful direction and there is no toggle for it.
        const { result } = compareOptional(
          rowA.compliance?.pct ?? null,
          rowB.compliance?.pct ?? null,
          (a, b) => a - b,
        );
        return result;
      }
      case 'sleep': {
        // Worst first, like every other measured ordering here: a trainer sorts by sleep to find
        // who is running short, not to celebrate who is rested. The mean is the SERVER's, never
        // recomputed — see `clientRow`.
        const { result } = compareOptional(
          rowA.avgSleepHours,
          rowB.avgSleepHours,
          (a, b) => a - b,
        );
        return result;
      }
      case 'mood': {
        const { result } = compareOptional(rowA.avgMood, rowB.avgMood, (a, b) => a - b);
        return result;
      }
      case 'lastWorkout': {
        // Oldest first, same reasoning. Timestamps are parsed rather than collated: an ISO string
        // happens to collate correctly for UTC and breaks the moment an offset appears.
        const { result } = compareOptional(rowA.lastWorkoutAt, rowB.lastWorkoutAt, (a, b) =>
          Date.parse(a) === Date.parse(b) ? 0 : Date.parse(a) - Date.parse(b),
        );
        return result;
      }
    }
  };

  return [...rows].sort((rowA, rowB) => {
    const byOrder = primary(rowA, rowB);
    if (byOrder !== 0) return byOrder;

    const byName = collator.compare(rowA.fullName, rowB.fullName);
    if (byName !== 0) return byName;
    return rowA.rowKey < rowB.rowKey ? -1 : rowA.rowKey > rowB.rowKey ? 1 : 0;
  });
}

/** Filter and order, in the order those two must happen. One call, one answer. */
export function rosterView(rows: readonly ClientRow[], query: RosterQuery): ClientRow[] {
  return orderClientRows(filterClientRows(rows, query), query.order, query.locale);
}
